import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { GAMES_KEY, type Redemption } from '../api';

type Update<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];

/**
 * ניהול משחקונים (PRD 5.8).
 *
 * מתן נקודות ידני נכתב ישירות ל points_ledger: ה RLS דורש
 * points.grant וגם ש granted_by יהיה העובד המחובר, ולכן אי אפשר
 * לתת נקודות בשם מישהו אחר ואי אפשר לתת לעצמך בלי שזה יירשם.
 */

export type AwardRow = {
  id: string;
  award_type_id: string;
  employee_id: string | null;
  branch_id: string | null;
  period_month: string;
  reason: string | null;
  image_path: string | null;
  announced_at: string | null;
  award_types: { name: string; target: 'employee' | 'branch' } | null;
  employees: { first_name: string; last_name: string } | null;
  branches: { name: string } | null;
};

export type Candidates = {
  month: string;
  employees: {
    employee_id: string;
    name: string;
    branch: string | null;
    points: number;
    courses: number;
  }[];
  branches: { branch_id: string; name: string; points: number }[];
};

const ADMIN_KEY = ['admin', 'games'] as const;

export function useAllRedemptions() {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'redemptions'],
    queryFn: async (): Promise<Redemption[]> => {
      const { data, error } = await supabase
        .from('reward_redemptions')
        .select(
          `id, reward_id, employee_id, status, created_at,
           rewards ( name, cost_points ),
           employees!reward_redemptions_employee_id_fkey ( first_name, last_name )`,
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Redemption[];
    },
  });
}

export function useAdminRewards() {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'rewards'],
    queryFn: async () => {
      const { data, error } = await supabase.from('rewards').select('*').order('cost_points');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAwards() {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'awards'],
    queryFn: async (): Promise<AwardRow[]> => {
      const { data, error } = await supabase
        .from('awards')
        .select(
          `id, award_type_id, employee_id, branch_id, period_month, reason, image_path,
           announced_at,
           award_types ( name, target ),
           employees ( first_name, last_name ),
           branches ( name )`,
        )
        .order('period_month', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AwardRow[];
    },
  });
}

export function useCandidates(month: string) {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'candidates', month],
    enabled: Boolean(month),
    queryFn: async (): Promise<Candidates | null> => {
      const { data, error } = await supabase.rpc('award_candidates', { p_month: month });
      if (error) throw error;
      return data as Candidates | null;
    },
  });
}

export function useAwardTypes() {
  return useQuery({
    queryKey: [...ADMIN_KEY, 'award-types'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('award_types')
        .select('id, name, target, default_points')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useGamesMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    void queryClient.invalidateQueries({ queryKey: GAMES_KEY });
  };

  const grantPoints = useMutation({
    mutationFn: async (input: {
      employeeId: string;
      points: number;
      reason: string;
      grantedBy: string;
    }) => {
      const { error } = await supabase.from('points_ledger').insert({
        employee_id: input.employeeId,
        points: input.points,
        reason: input.reason,
        granted_by: input.grantedBy,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const decideRedemption = useMutation({
    mutationFn: async (input: { id: string; approve: boolean }) => {
      const { error } = await supabase.rpc('decide_redemption', {
        p_redemption: input.id,
        p_approve: input.approve,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const markDelivered = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('reward_redemptions')
        .update({ status: 'delivered' })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  // יצירה ועדכון נפרדים: ביצירה שם ומחיר חובה, בעדכון הכל אופציונלי
  const createReward = useMutation({
    mutationFn: async (input: Database['public']['Tables']['rewards']['Insert']) => {
      const { error } = await supabase.from('rewards').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const updateReward = useMutation({
    mutationFn: async (input: { id: string; patch: Update<'rewards'> }) => {
      const { error } = await supabase.from('rewards').update(input.patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeReward = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('rewards').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const createCompetition = useMutation({
    mutationFn: async (input: Database['public']['Tables']['competitions']['Insert']) => {
      const { error } = await supabase.from('competitions').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeCompetition = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('competitions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const closeCompetition = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('close_competition', { p_competition: id });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const announceCompetition = useMutation({
    mutationFn: async (input: { id: string; channelId: string }) => {
      const { error } = await supabase.rpc('announce_competition', {
        p_competition: input.id,
        p_channel: input.channelId,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const createAward = useMutation({
    mutationFn: async (input: {
      award_type_id: string;
      employee_id: string | null;
      branch_id: string | null;
      period_month: string;
      reason: string | null;
      created_by: string;
    }) => {
      const { error } = await supabase.from('awards').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const announceAward = useMutation({
    mutationFn: async (input: { id: string; channelId: string }) => {
      const { error } = await supabase.rpc('announce_award', {
        p_award: input.id,
        p_channel: input.channelId,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeAward = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('awards').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return {
    grantPoints,
    decideRedemption,
    markDelivered,
    createReward,
    updateReward,
    removeReward,
    createCompetition,
    removeCompetition,
    closeCompetition,
    announceCompetition,
    createAward,
    announceAward,
    removeAward,
  };
}

/** החודש הקודם כברירת מחדל: מצטיין נבחר בדיעבד, לא באמצע החודש. */
export function lastMonthKey(): string {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}
